import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    CalendarMonth as ScheduleIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { STATUS_CONFIG, PRIORITY_CONFIG, formatDate, isOverdue } from "./constants";

function linkedLabel(item) {
    if (item.project_detail) {
        return `${item.project_detail.number} · ${item.project_detail.name}`;
    }
    if (item.quote_detail) {
        return `${item.quote_detail.number} · ${item.quote_detail.name}`;
    }
    return null;
}

export default function ScheduleItemsList({ items, loading, onEdit, onDelete }) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (items.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <ScheduleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No schedule items found.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {items.map((item) => {
                const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.not_started;
                const priorityCfg = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG[2];
                const overdue = isOverdue(item.due_date, item.status);
                const linked = linkedLabel(item);

                return (
                    <Card
                        key={item.id}
                        sx={{
                            boxShadow: "none",
                            border: 1,
                            borderColor: "primary.main",
                        }}
                    >
                        <CardActionArea onClick={() => onEdit(item)}>
                            <CardContent
                                sx={{
                                    py: 1.5,
                                    "&:last-child": { pb: 1.5 },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                                    >
                                        {item.task_type_detail?.name || "Task"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {item.assigned_to_detail?.full_name ||
                                            item.assigned_to_detail?.username}
                                        {linked ? ` · ${linked}` : ""}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mt: 0.75,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Chip
                                            icon={statusCfg.icon}
                                            label={statusCfg.label}
                                            size="small"
                                            color={statusCfg.color}
                                        />
                                        <Chip
                                            label={priorityCfg.label}
                                            size="small"
                                            sx={{
                                                bgcolor: priorityCfg.color,
                                                color: "#fff",
                                            }}
                                        />
                                        {item.due_date && (
                                            <Chip
                                                label={`Due ${formatDate(item.due_date)}`}
                                                size="small"
                                                color={overdue ? "error" : "default"}
                                                variant={overdue ? "filled" : "outlined"}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
                            </CardContent>
                        </CardActionArea>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                px: 1,
                                pb: 1,
                                gap: 0.5,
                            }}
                        >
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => onEdit(item)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                            <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Card>
                );
            })}
        </Box>
    );
}
